import { useCallback, useState } from 'react';
import { getProactiveMessageApi } from '@/api/aiChatApi';
import { store } from '@/config/store';
import { ProactiveMessage } from '@/types/api/aiChat';

interface UseProactiveMessageResult {
  proactiveMessage: ProactiveMessage | null;
  isLoading: boolean;
  error: string | null;
  fetchProactiveMessage: () => Promise<ProactiveMessage | null>;
}

/**
 * プロアクティブメッセージ取得フック
 * ホーム画面表示時にAIからの能動的なメッセージを取得する
 */
export const useProactiveMessage = (): UseProactiveMessageResult => {
  const [proactiveMessage, setProactiveMessage] =
    useState<ProactiveMessage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProactiveMessage =
    useCallback(async (): Promise<ProactiveMessage | null> => {
      setIsLoading(true);
      setError(null);

      try {
        // ログインしていない場合は取得しない
        if (!store.state.authInfo?.m_customer_id) {
          setProactiveMessage(null);
          return null;
        }

        const res = await getProactiveMessageApi();

        if (res.data?.success && res.data.data) {
          setProactiveMessage(res.data.data);
          return res.data.data;
        }

        // APIエラーでもメッセージ非表示として扱う（致命的エラーではない）
        setProactiveMessage(null);
        return null;
      } catch (e) {
        // プロアクティブメッセージの取得失敗はサイレントに処理
        // ホーム画面の表示を妨げないため、コンソールログのみ
        console.error('プロアクティブメッセージ取得エラー:', e);
        setError(
          e instanceof Error
            ? e.message
            : 'プロアクティブメッセージ取得中にエラーが発生しました',
        );
        setProactiveMessage(null);
        return null;
      } finally {
        setIsLoading(false);
      }
    }, []);

  return {
    proactiveMessage,
    isLoading,
    error,
    fetchProactiveMessage,
  };
};
